/**
 * Graph Visualizer Module
 * Handles visualization of process graphs using Cytoscape.js
 */
import cytoscape from 'cytoscape';
import * as GraphUtils from './graphUtils.js';

export class GraphVisualizer {
    constructor(containerId) {
        this.containerId = containerId;
        this.cy = null;
        this.processData = null;
        this.originalElements = null;
        this.edgeFilterPercentage = 100; // Default: show all edges
        this.nodeFilterPercentage = 100; // Default: show all nodes
        this.processStartId = null; // Dynamic process start identifier
        this.processEndId = null; // Dynamic process end identifier
        
        // Extract widget ID from container ID for scoped element selection
        this.widgetId = containerId.replace('graph-container-', '');
        
        this.nodeColors = {
            default: '#3498db',
            start: '#2ecc71',
            end: '#e74c3c',
            rare: '#95a5a6'
        };
        this.edgeColors = {
            default: '#95a5a6',
            highlighted: '#8e44ad'
        };
        this.cytoscapeOptions = {
            wheelSensitivity: 3,
            minZoom: 0.2,
            maxZoom: 2.0
        };
    }

    /**
     * Initialize Cytoscape graph
     */
    init() {
        console.log('GraphVisualizer.init() called');
        console.log('Container ID:', this.containerId);
        
        try {
            // Check if container exists
            const container = document.getElementById(this.containerId);
            console.log('Container element:', container);
            console.log('Container exists:', !!container);
            
            if (!container) {
                console.error(`GraphVisualizer: Container with ID '${this.containerId}' not found`);
                return;
            }
            
            console.log('Container dimensions:', {
                width: container.offsetWidth,
                height: container.offsetHeight,
                clientWidth: container.clientWidth,
                clientHeight: container.clientHeight
            });
            
            console.log('Creating Cytoscape instance...');
            this.cy = cytoscape({
                container: container,
                style: this.getGraphStylesheet(),
                wheelSensitivity: this.cytoscapeOptions.wheelSensitivity,
                minZoom: this.cytoscapeOptions.minZoom,
                maxZoom: this.cytoscapeOptions.maxZoom
            });
            
            console.log('Cytoscape instance created:', !!this.cy);
            console.log('Cytoscape container:', this.cy ? this.cy.container() : 'N/A');
            
            this.addEventListeners();
            console.log('Event listeners added');
            
        } catch (error) {
            console.error('Error in GraphVisualizer.init():', error);
            console.error('Error stack:', error.stack);
        }
    }

    /**
     * Ensure Cytoscape instance is properly initialized with renderer ready
     * @returns {Promise} - Promise that resolves when initialization is complete
     */
    async ensureInitialized() {
        return new Promise((resolve, reject) => {
            console.log('ensureInitialized called');
            
            if (this.cy && this.cy.renderer && this.cy.renderer()) {
                console.log('Cytoscape already initialized with renderer ready');
                resolve();
                return;
            }

            if (!this.cy) {
                console.log('Cytoscape instance does not exist, initializing...');
                this.init();
            }

            if (!this.cy) {
                console.error('Failed to create Cytoscape instance');
                reject(new Error('Failed to create Cytoscape instance'));
                return;
            }

            const container = document.getElementById(this.containerId);
            if (!container) {
                console.error(`Container with ID '${this.containerId}' not found`);
                reject(new Error(`Container with ID '${this.containerId}' not found`));
                return;
            }

            // Wait for container to have proper dimensions and renderer to be ready
            const checkRendererReady = () => {
                if (container.offsetWidth > 0 &&
                    container.offsetHeight > 0 &&
                    this.cy &&
                    this.cy.renderer &&
                    this.cy.renderer()) {
                    console.log('Container and renderer are ready');
                    resolve();
                } else {
                    console.log('Waiting for container dimensions and renderer...');
                    requestAnimationFrame(checkRendererReady);
                }
            };

            checkRendererReady();
        });
    }

    /**
     * Check if Cytoscape instance and renderer are ready for operations
     * @returns {boolean} - True if ready, false otherwise
     */
    isRendererReady() {
        return this.cy &&
               this.cy.renderer &&
               this.cy.renderer() &&
               !this.cy.destroyed();
    }

    /**
     * Generate Cytoscape stylesheet
     * @returns {Array} - Cytoscape style array
     */
    getGraphStylesheet() {
        return [
            {
                selector: 'node',
                style: {
                    'label': function(ele) {
                        const activityName = ele.data('label') || ele.data('id');
                        const countText = ele.data('countText') || '';
                        return countText ? `${activityName}\n${countText}` : activityName;
                    },
                    'text-valign': 'center',
                    'text-halign': 'center',
                    'background-color': this.nodeColors.default,
                    'color': 'white',
                    'font-size': '12px',
                    'text-wrap': 'wrap',
                    'text-max-width': '120px',

                    'width': function(ele) { return ele.data('label').length * 6.5; },
                    // #TODO: at some point this should probably use the text-max-width for height calculation
                    'height': function(ele) {return (ele.data('label').split('\n').length + 1) * 14;},

                    'shape': 'roundrectangle',
                    'border-width': 2,
                    'border-color': 'white',
                    // 'text-margin-y': 2,
                    'padding': '5px',
                    'text-outline-width': 1,
                    'text-outline-color': 'data(color)',
                    'overlay-padding': '10px',
                    'text-justification': 'center'
                }
            },
            {
                selector: 'node.start',
                style: {
                    'background-color': this.nodeColors.start,
                    'border-color': 'white',
                    'color': 'white',
                    'shape': 'roundrectangle',
                }
            },
            {
                selector: 'node.end',
                style: {
                    'background-color': this.nodeColors.end,
                    'border-color': 'white',
                    'color': 'white',
                    'shape': 'roundrectangle',
                }
            },
            {
                selector: 'node.rare',
                style: {
                    'background-color': this.nodeColors.rare,
                    'shape': 'roundrectangle',
                    'border-color': 'white',
                    'color': 'white',
                    'opacity': 0.8
                }
            },
            {
                selector: 'node:selected',
                style: {
                    'border-width': 3,
                    'border-color': '#f39c12',
                    'border-opacity': 1
                }
            },
            {
                selector: 'edge',
                style: {
                    'width': 'data(width)',
                    'line-color': this.edgeColors.default,
                    'target-arrow-color': this.edgeColors.default,
                    'target-arrow-shape': 'triangle',



                     'curve-style': function(ele) {
                        // Use loop style for self-loops
                        return ele.data('source') === ele.data('target') ? 'loop' : 'unbundled-bezier';
                    },
                    'loop-direction': '0deg',
                    'loop-sweep': '45deg',
                    'control-point-distances': function(ele) {
                        return ele.data('source') === ele.data('target') ? '0' : (ele.data('point-distances') || '0');
                    },
                    'control-point-weights': function(ele) {
                        return ele.data('source') === ele.data('target') ? '0.5' : (ele.data('point-weights') || '0.5');
                    },

                    // 'curve-style': 'unbundled-bezier',
                    // 'control-point-distances': function(ele) {
                    //     return ele.data('point-distances') || '0';
                    // },
                    // 'control-point-weights': function(ele) {
                    //     return ele.data('point-weights') || '0.5';
                    // },


                    'label': 'data(label)',
                    'font-size': '10px',
                    'color': '#666',
                    // 'text-rotation': 'autorotate',
                    'text-margin-y': -10,
                    'text-background-color': 'white',
                    'text-background-opacity': 0.7,
                    'text-background-padding': '2px'
                }
            },
            {
                selector: 'edge.highlighted',
                style: {
                    'line-color': this.edgeColors.highlighted,
                    'target-arrow-color': this.edgeColors.highlighted,
                    'width': 'data(highlightWidth)',
                    'z-index': 999
                }
            },
            {
                selector: 'edge.process-boundary',
                style: {
                    'line-style': 'dashed',
                    'opacity': 0.5,
                    'line-color': 'rgba(149, 165, 166, 0.5)',
                    'target-arrow-color': 'rgba(149, 165, 166, 0.5)',
                    'width': '1px',
                }
            }
        ];
    }

    /**
     * Add event listeners for user interaction
     */
    addEventListeners() {
        const self = this;
        
        // Only add Cytoscape event listeners if renderer is ready
        if (this.isRendererReady()) {
            this.cy.on('tap', 'node', function(evt) {
                const node = evt.target;
                self.highlightConnectedEdges(node);
                self.updateMetricsPanel(node);
                console.log("Node tapped:", node.data());
            });
            
            this.cy.on('tap', function(evt) {
                if (evt.target === this) {
                    // Clicked on background - reset highlighting
                    self.resetHighlighting();
                    self.updateMetricsPanel();
                }
            });
        }
        
        // Attach zoom button listeners
        const zoomInBtn = document.getElementById(`zoom-in-${this.widgetId}`);
        const zoomOutBtn = document.getElementById(`zoom-out-${this.widgetId}`);
        
        if (zoomInBtn) {
            zoomInBtn.addEventListener('click', () => {
                if (this.isRendererReady()) {
                    this.cy.zoom({
                        level: this.cy.zoom() * 1.2,
                        renderedPosition: { x: this.cy.width() / 2, y: this.cy.height() / 2 }
                    });
                }
            });
        }
        
        if (zoomOutBtn) {
            zoomOutBtn.addEventListener('click', () => {
                if (this.isRendererReady()) {
                    this.cy.zoom({
                        level: this.cy.zoom() * 0.8,
                        renderedPosition: { x: this.cy.width() / 2, y: this.cy.height() / 2 }
                    });
                }
            });
        }
        
        // Attach edge filter listener
        const edgeFilterSlider = document.getElementById(`edge-filter-${this.widgetId}`);
        const edgeFilterValueDisplay = document.getElementById(`edge-filter-value-${this.widgetId}`);
        
        if (edgeFilterSlider) {
            edgeFilterSlider.addEventListener('input', async (event) => {
                this.edgeFilterPercentage = parseInt(event.target.value, 10);
                // Calculate minimum edge filter percentage
                const minEdgePercentage = GraphUtils.calculateMinEdgeFilterPercentage(
                    this.processData,
                    this.nodeFilterPercentage,
                    this.processStartId,
                    this.processEndId
                );
                // Apply minimum if needed
                if (this.edgeFilterPercentage < minEdgePercentage) {
                    this.edgeFilterPercentage = minEdgePercentage;
                    edgeFilterSlider.value = minEdgePercentage;
                }
                if (edgeFilterValueDisplay) {
                    edgeFilterValueDisplay.textContent = `${this.edgeFilterPercentage}%`;
                }
                await this.applyFilters();
            });
        }
        
        // Attach node filter listener
        const nodeFilterSlider = document.getElementById(`node-filter-${this.widgetId}`);
        const nodeFilterValueDisplay = document.getElementById(`node-filter-value-${this.widgetId}`);
        
        if (nodeFilterSlider) {
            nodeFilterSlider.addEventListener('input', async (event) => {
                this.nodeFilterPercentage = parseInt(event.target.value, 10);
                // Calculate minimum node filter percentage
                const minNodePercentage = GraphUtils.calculateMinNodeFilterPercentage(
                    this.processData,
                    this.processStartId,
                    this.processEndId
                );
                // Apply minimum if needed
                if (this.nodeFilterPercentage < minNodePercentage) {
                    this.nodeFilterPercentage = minNodePercentage;
                    nodeFilterSlider.value = minNodePercentage;
                }
                if (nodeFilterValueDisplay) {
                    nodeFilterValueDisplay.textContent = `${this.nodeFilterPercentage}%`;
                }
                await this.applyFilters();
            });
        }
    }

    /**
     * Apply edge filter based on the current filter percentage
     */
    /**
     * Apply all filters (node and edge) and handle edge recovery
     */
    async applyFilters() {
        if (!this.processData) return;
        
        // Check if Cytoscape instance exists and renderer is ready
        if (!this.isRendererReady()) {
            console.warn("Cannot apply filters: Cytoscape renderer not ready");
            return;
        }
        
        // Check and update minimum filter values
        this.updateMinimumFilterValues();
        
        try {
            console.log(`Applying filters: Node ${this.nodeFilterPercentage}%, Edge ${this.edgeFilterPercentage}%`);
            console.log("Regenerating graph with DOT converter using current filter settings...");
            
            // Create DOT string with current filter percentages
            const dotString = GraphUtils.create_dot_string(
                this.processData,
                this.nodeFilterPercentage,
                this.edgeFilterPercentage,
                this.processStartId,
                this.processEndId
            );
            
            console.debug("Generated filtered DOT string:", dotString);
            
            // Convert DOT string to Cytoscape elements using the DOT converter
            const convertedElements = await GraphUtils.convertDotString(dotString);
            
            console.debug("DOT converter returned filtered elements:", convertedElements);
            
            // Transform converted elements to match our expected format
            const elements = this.transformConvertedElements(convertedElements, this.processData);
            
            // Clear existing graph and add new filtered elements
            this.cy.elements().remove();
            this.cy.add(elements);
            
            // Update original elements to the filtered set for consistency
            this.originalElements = elements;
            
            // Apply layout with defensive checks - use preset since we have DOT-calculated positions
            if (this.isRendererReady() && elements.length > 0) {
                const layout = this.cy.layout({
                    name: 'preset',
                    padding: 50
                });
                
                // Add error handling for layout run
                try {
                    layout.run();
                    
                    // Center the graph only if renderer is still ready
                    if (this.isRendererReady()) {
                        this.cy.fit();
                        this.cy.center();
                    }
                } catch (layoutError) {
                    console.error("Error running layout:", layoutError);
                    // Try a simpler layout as fallback
                    try {
                        if (this.isRendererReady()) {
                            const fallbackLayout = this.cy.layout({ name: 'grid' });
                            fallbackLayout.run();
                        }
                    } catch (fallbackError) {
                        console.error("Fallback layout also failed:", fallbackError);
                    }
                }
            } else {
                console.warn("Skipping layout: renderer not ready or no elements to layout");
            }
            
            console.log(`Filter application complete: ${elements.filter(e => e.group === 'nodes').length} nodes, ${elements.filter(e => e.group === 'edges').length} edges`);
            
        } catch (error) {
            console.error("Error during DOT conversion in filter application", error);
        }
    }
    
    /**
     * Add edge to the graph
     * @param {Object} edge - Edge object with source, target, and count
     */
    addEdgeToGraph(edge) {
        const { source, target, count } = edge;
        
        // Get timing information if available
        let timingLabel = '';
        if (this.processData.timingMetrics &&
            this.processData.timingMetrics[source] &&
            this.processData.timingMetrics[source][target]) {
            
            const hours = this.processData.timingMetrics[source][target].avgDurationHours;
            timingLabel = `${hours.toFixed(1)} hours`;
        }
        
        // Determine edge width based on count relative to other edges
        const edgeCounts = this.processData.edges.map(e => e.count);
        const maxCount = Math.max(...edgeCounts);
        const minCount = Math.min(...edgeCounts);
        const countRange = maxCount - minCount;
        
        // Edge width based on count (1-8px)
        const width = countRange > 0
            ? 1 + ((count - minCount) / countRange) * 7
            : 4;
        
        // Create the edge
        this.cy.add({
            group: 'edges',
            data: {
                id: `${source}-${target}`,
                source: source,
                target: target,
                count: count,
                width: width,
                highlightWidth: Math.min(width * 1.5, 10),
                label: count > 1 ? timingLabel : ''
            }
        });
    }
    
    /**
     * Update sliders to reflect minimum filter values
     */
    updateMinimumFilterValues() {
        // Calculate minimum values
        const minNodePercentage = GraphUtils.calculateMinNodeFilterPercentage(
            this.processData,
            this.processStartId,
            this.processEndId
        );
        const minEdgePercentage = GraphUtils.calculateMinEdgeFilterPercentage(
            this.processData,
            this.nodeFilterPercentage,
            this.processStartId,
            this.processEndId
        );
        
        // Always enforce the minimum node percentage
        const nodeFilterSlider = document.getElementById(`node-filter-${this.widgetId}`);
        const nodeFilterValueDisplay = document.getElementById(`node-filter-value-${this.widgetId}`);
        
        // Set node filter minimum and update slider
        if (this.nodeFilterPercentage < minNodePercentage) {
            this.nodeFilterPercentage = minNodePercentage;
            if (nodeFilterSlider) {
                nodeFilterSlider.value = minNodePercentage;
            }
            if (nodeFilterValueDisplay) {
                nodeFilterValueDisplay.textContent = `${minNodePercentage}%`;
            }
        }
        
        // Always enforce the minimum edge percentage
        const edgeFilterSlider = document.getElementById(`edge-filter-${this.widgetId}`);
        const edgeFilterValueDisplay = document.getElementById(`edge-filter-value-${this.widgetId}`);
        
        // Set edge filter minimum and update slider
        if (this.edgeFilterPercentage < minEdgePercentage) {
            this.edgeFilterPercentage = minEdgePercentage;
            if (edgeFilterSlider) {
                edgeFilterSlider.value = minEdgePercentage;
            }
            if (edgeFilterValueDisplay) {
                edgeFilterValueDisplay.textContent = `${minEdgePercentage}%`;
            }
        }
        
        // Log minimums for debugging
        console.log(`Calculated minimum filter percentages - Node: ${minNodePercentage}%, Edge: ${minEdgePercentage}%`);
    }

    /**
     * Render process graph from analyzed data
     * @param {Object} processData - Analyzed process data
     */
    async renderGraph(processData) {
        // Ensure Cytoscape is properly initialized before proceeding
        if (!this.cy) {
            await this.ensureInitialized();
        }
        
        // Double-check that initialization was successful
        if (!this.isRendererReady()) {
            console.error("Cannot render graph: Cytoscape renderer not ready after initialization");
            return;
        }
        
        this.processData = processData;
        
        // Set the dynamic process start/end identifiers from the data
        this.setProcessIdentifiers(processData);
        
        this.cy.elements().remove(); // Clear existing graph
        
        try {
            console.log("Converting process data to DOT format and rendering via DOT converter...");
            
            // Create DOT string from process data (using 100% filters to get all elements)
            const dotString = GraphUtils.create_dot_string(
                processData,
                100, // nodeFilterPercentage - get all nodes for DOT conversion
                100, // edgeFilterPercentage - get all edges for DOT conversion
                this.processStartId,
                this.processEndId
            );
            
            console.debug("Generated DOT string:", dotString);
            
            // Convert DOT string to Cytoscape elements using the DOT converter
            const convertedElements = await GraphUtils.convertDotString(dotString);
            
            console.debug("DOT converter returned elements:", convertedElements);
            
            // Transform converted elements to match our expected format
            const elements = this.transformConvertedElements(convertedElements, processData);
            
            // Store original elements for filtering
            this.originalElements = elements;
            
            // Check if Cytoscape instance is still ready before adding elements
            if (!this.isRendererReady()) {
                console.error("Cannot add elements: Cytoscape renderer not ready");
                return;
            }
            
            this.cy.add(elements);
            
            // Apply filters (which also applies layout and centers the graph)
            await this.applyFilters();
            
            // Set the minimum filter values for the sliders
            this.updateMinimumFilterValues();
            
            // Update metrics panel with overall metrics
            this.updateMetricsPanel();
            
            // Log the most common path for debugging
            if (this.processData.mostCommonPath) {
                console.log("Most common path:", this.processData.mostCommonPath);
            }
            
        } catch (error) {
            console.error("Error in DOT conversion workflow, falling back to direct rendering:", error);
            
            // Fallback to original direct rendering method
            try {
                if (this.isRendererReady()) {
                    const elements = this.createGraphElements(processData);
                    this.originalElements = elements;
                    this.cy.add(elements);
                    await this.applyFilters();
                    this.updateMinimumFilterValues();
                    this.updateMetricsPanel();
                } else {
                    console.error("Cannot fallback to direct rendering: renderer not ready");
                }
            } catch (fallbackError) {
                console.error("Fallback rendering also failed:", fallbackError);
            }
        }
    }

    /**
     * Transform converted elements from DOT converter to match our expected format
     * @param {Object} convertedElements - Elements from DOT converter
     * @param {Object} processData - Original process data for additional attributes
     * @returns {Array} - Array of Cytoscape elements with proper styling
     */
    transformConvertedElements(convertedElements, processData) {
        console.log("Transforming converted elements to match expected format...");
        
        const elements = [];
        const { activities, processStartActivities, processEndActivities } = processData;

        // #FILTER_OPTION        
        // Determine count thresholds for node sizing
        const nodeCounts = Object.values(activities).map(activity => activity.count || 0);
        const maxNodeCount = Math.max(...nodeCounts);
        const minNodeCount = Math.min(...nodeCounts);
        const nodeCountRange = maxNodeCount - minNodeCount;
        const nodeRareThreshold = maxNodeCount * 0.05;

        // Transform nodes
        if (convertedElements.nodes) {
            convertedElements.nodes.forEach(node => {
                const nodeId = node.data.id;
                const count = activities[nodeId].count || 0;
                const isStart = processStartActivities.some(a => a.activity === nodeId);
                const isEnd = processEndActivities.some(a => a.activity === nodeId);
                const isRare = count <= nodeRareThreshold;

                // Size nodes based on count (30-60px)
                const size = nodeCountRange > 0
                    ? 30 + ((count - minNodeCount) / nodeCountRange) * 30
                    : 45;
                
                // Determine node classes
                const classes = [];
                if (isStart) classes.push('start');
                if (isEnd) classes.push('end');
                if (isRare) classes.push('rare');
                
                // Create transformed node with proper label handling
                const rawLabel = node.data.label || nodeId;
                
                // Split the label on \n to separate activity name and count
                let activityName = nodeId;
                let countText = '';
                
                if (typeof rawLabel === 'string' && rawLabel.includes('\\n')) {
                    const parts = rawLabel.split('\\n');
                    activityName = parts[0] || nodeId;
                    countText = parts[1] || '';
                } else if (typeof rawLabel === 'string' && rawLabel.includes('\n')) {
                    const parts = rawLabel.split('\n');
                    activityName = parts[0] || nodeId;
                    countText = parts[1] || '';
                } else {
                    activityName = rawLabel;
                }
                
                // Create a clean label with just the activity name for now
                // We'll handle the frequency display in CSS or as a separate element
                const transformedNode = {
                    group: 'nodes',
                    data: {
                        id: nodeId,
                        label: activityName,
                        count: count,
                        countText: countText,
                        size: size,
                        color: this.getNodeColor(isStart, isEnd, isRare)
                    },
                    position: node.position || { x: 0, y: 0 },
                    classes: classes.join(' ')
                };
                
                elements.push(transformedNode);
            });
        }
        
        // Transform edges
        if (convertedElements.edges) {
            // Get edge counts for width calculation
            const edgeCounts = processData.edges.map(e => e.count);
            const edgeMaxCount = Math.max(...edgeCounts);
            const edgeMinCount = Math.min(...edgeCounts);
            const edgeCountRange = edgeMaxCount - edgeMinCount;
            
            convertedElements.edges.forEach(edge => {
                const edgeId = edge.data.id;
                const sourceId = edge.data.source;
                const targetId = edge.data.target;
                
                // Find original edge data for count information
                const originalEdge = processData.edges.find(e =>
                    e.source === sourceId && e.target === targetId
                );
                const count = originalEdge ? originalEdge.count : 1;
                
                // Calculate edge width based on count (1-8px)
                const width = edgeCountRange > 0
                    ? 1 + ((count - edgeMinCount) / edgeCountRange) * 7
                    : 4;
                
                // Get timing information if available
                let timingLabel = '';
                if (processData.timingMetrics &&
                    processData.timingMetrics[sourceId] &&
                    processData.timingMetrics[sourceId][targetId]) {
                    
                    const hours = processData.timingMetrics[sourceId][targetId].avgDurationHours;
                    timingLabel = `${hours.toFixed(1)} hours`;
                }
                
                // Determine if this is an edge from Process Start or to Process End
                const isFromStart = sourceId === this.processStartId;
                const isToEnd = targetId === this.processEndId;
                const edgeClass = (isFromStart || isToEnd) ? 'process-boundary' : '';
                
                // Process-boundary edges should not display labels
                const edgeLabel = edgeClass ? '' : (count > 1 ? timingLabel : '');
                
                // Create transformed edge
                const transformedEdge = {
                    group: 'edges',
                    data: {
                        id: edgeId,
                        source: sourceId,
                        target: targetId,
                        count: count,
                        width: width,
                        highlightWidth: Math.min(width * 1.5, 10),
                        label: edgeLabel
                    }
                };
                
                // Add process-boundary class if applicable
                if (edgeClass) {
                    transformedEdge.classes = edgeClass;
                }
                
                // Preserve control points from DOT converter if available
                if (edge.data['point-distances']) {
                    transformedEdge.data['point-distances'] = edge.data['point-distances'];
                }
                if (edge.data['point-weights']) {
                    transformedEdge.data['point-weights'] = edge.data['point-weights'];
                }
                
                elements.push(transformedEdge);
            });
        }
        
        console.log(`Transformed ${elements.filter(e => e.group === 'nodes').length} nodes and ${elements.filter(e => e.group === 'edges').length} edges`);
        return elements;
    }

    /**
     * Get node color based on node type
     * @param {boolean} isStart - Whether node is a start node
     * @param {boolean} isEnd - Whether node is an end node
     * @param {boolean} isRare - Whether node is rare
     * @returns {string} - Color hex code
     */
    getNodeColor(isStart, isEnd, isRare) {
        if (isStart) return this.nodeColors.start;
        if (isEnd) return this.nodeColors.end;
        if (isRare) return this.nodeColors.rare;
        return this.nodeColors.default;
    }

    /**
     * Set the process start and end identifiers from the process data
     * @param {Object} processData - Analyzed process data
     */
    setProcessIdentifiers(processData) {
        // Use the identifiers from processData if available
        if (processData.processStartId) {
            this.processStartId = processData.processStartId;
        }
        
        if (processData.processEndId) {
            this.processEndId = processData.processEndId;
        }
        
        // Fallback: use most common path
        if (!this.processStartId && processData.mostCommonPath && processData.mostCommonPath.length > 0) {
            this.processStartId = processData.mostCommonPath[0];
        }
        
        if (!this.processEndId && processData.mostCommonPath && processData.mostCommonPath.length > 0) {
            this.processEndId = processData.mostCommonPath[processData.mostCommonPath.length - 1];
        }
    }

    /**
     * Create graph elements from process data
     * @param {Object} processData - Analyzed process data
     * @returns {Array} - Array of Cytoscape elements
     */
    createGraphElements(processData) {
        const elements = [];
        const { activities, edges, processStartActivities, processEndActivities } = processData;
        
        // Determine count thresholds for node sizing
        const activityCounts = Object.values(activities).map(activity => activity.count || 0);
        const activityMaxCount = Math.max(...activityCounts);
        const activityMinCount = Math.min(...activityCounts);
        const activityCountRange = activityMaxCount - activityMinCount;

        // Determine rare activity threshold (< 5% of max frequency)
        const activityRareThreshold = activityMaxCount * 0.05;

        // Create nodes for activities
        Object.entries(activities).forEach(([activity, activityDetails]) => {
            const count = activityDetails.count || 0;
            const isStart = processStartActivities.some(a => a.activity === activity);
            const isEnd = processEndActivities.some(a => a.activity === activity);
            const isRare = count <= activityRareThreshold;

            // Size nodes based on frequency (30-60px)
            const size = activityCountRange > 0 
                ? 30 + ((count - activityMinCount) / activityCountRange) * 30 
                : 45;
            
            // Determine node class and color
            let nodeClass = '';
            let color = this.nodeColors.default;
            
            if (isStart) {
                nodeClass = 'start';
                color = this.nodeColors.start;
            } else if (isEnd) {
                nodeClass = 'end';
                color = this.nodeColors.end;
            } else if (isRare) {
                nodeClass = 'rare';
                color = this.nodeColors.rare;
            }
            
            elements.push({
                data: {
                    id: activity,
                    label: `${activity}\n${count}`,
                    count: count,
                    size: size,
                    color: color
                },
                classes: nodeClass
            });
        });
        
        // Determine edge thickness range based on counts
        const edgeCounts = edges.map(e => e.count);
        const edgeMaxCount = Math.max(...edgeCounts);
        const edgeMinCount = Math.min(...edgeCounts);
        const edgeCountRange = edgeMaxCount - edgeMinCount;
        
        // Create edges
        edges.forEach(edge => {
            const { source, target, count } = edge;
            
            // Edge width based on count (1-8px)
            const width = edgeCountRange > 0
                ? 1 + ((count - edgeMinCount) / edgeCountRange) * 7
                : 4;
            
            // Get timing information if available
            let timingLabel = '';
            if (processData.timingMetrics && 
                processData.timingMetrics[source] && 
                processData.timingMetrics[source][target]) {
                
                const hours = processData.timingMetrics[source][target].avgDurationHours;
                timingLabel = `${hours.toFixed(1)} hours`;
            }
            
            // Determine if this is an edge from Process Start or to Process End
            const isFromStart = source === this.processStartId;
            const isToEnd = target === this.processEndId;
            const edgeClass = (isFromStart || isToEnd) ? 'process-boundary' : '';
            
            // Process-boundary edges should not display labels
            const edgeLabel = edgeClass ? '' : (count > 1 ? `${timingLabel}` : '');

            elements.push({
                data: {
                    id: `${source}-${target}`,
                    source: source,
                    target: target,
                    count: count,
                    width: width,
                    highlightWidth: Math.min(width * 1.5, 10),
                    label: edgeLabel
                },
                classes: edgeClass
            });
        });
        
        return elements;
    }

    /**
     * Highlight edges connected to a node
     * @param {Object} node - Cytoscape node
     */
    highlightConnectedEdges(node) {
        if (!this.isRendererReady()) {
            console.warn("Cannot highlight edges: renderer not ready");
            return;
        }
        
        // Reset existing highlighting
        this.resetHighlighting();
        
        // Highlight incoming and outgoing edges
        const connectedEdges = node.connectedEdges();
        connectedEdges.addClass('highlighted');
        
        // Optional: highlight connected nodes
        // const connectedNodes = node.neighborhood().nodes();
        // connectedNodes.addClass('highlighted');
    }

    /**
     * Reset all highlighting
     */
    resetHighlighting() {
        if (!this.isRendererReady()) {
            console.warn("Cannot reset highlighting: renderer not ready");
            return;
        }
        
        this.cy.elements().removeClass('highlighted');
    }

    /**
     * Update metrics panel with selected node or overall metrics
     * @param {Object} node - Selected Cytoscape node (optional)
     */
    updateMetricsPanel(node = null) {
        const metricsContent = document.getElementById(`metrics-content-${this.widgetId}`);
        
        // If metrics panel is not shown, skip updating
        if (!metricsContent) {
            return;
        }
        
        if (!this.processData) {
            metricsContent.innerHTML = '<p>No data available</p>';
            return;
        }
        
        if (node) {
            // Show node-specific metrics
            const activity = node.id();
            const count = node.data('count');
            const totalCases = this.processData.totalCases;
            const percentOfCases = ((count / totalCases) * 100).toFixed(1);

            // Find incoming and outgoing connections
            const incoming = this.getIncomingConnections(activity);
            const outgoing = this.getOutgoingConnections(activity);
            
            let html = `
                <h3>${activity}</h3>
                <div class="metric">
                    <div class="metric-label">Count:</div>
                    <div class="metric-value">${count} (${percentOfCases}%)</div>
                </div>
            `;
            
            // Incoming activities
            if (incoming.length > 0) {
                html += '<h4>Incoming From:</h4>';
                html += '<ul>';
                incoming.forEach(conn => {
                    html += `<li>${conn.source} (${conn.count})</li>`;
                });
                html += '</ul>';
            }
            
            // Outgoing activities
            if (outgoing.length > 0) {
                html += '<h4>Outgoing To:</h4>';
                html += '<ul>';
                outgoing.forEach(conn => {
                    html += `<li>${conn.target} (${conn.count})</li>`;
                });
                html += '</ul>';
            }
            
            metricsContent.innerHTML = html;
        } else {
            // Show overall process metrics
            const { totalCases, activities, edges, processStartActivities, processEndActivities } = this.processData;
            
            let html = `
                <div class="metric">
                    <div class="metric-label">Total Cases:</div>
                    <div class="metric-value">${totalCases}</div>
                </div>
                <div class="metric">
                    <div class="metric-label">Activities:</div>
                    <div class="metric-value">${Object.keys(activities).length}</div>
                </div>
                <div class="metric">
                    <div class="metric-label">Connections:</div>
                    <div class="metric-value">${edges.length}</div>
                </div>
            `;
            
            // Top start activities
            html += '<h4>Top Starting Activities:</h4>';
            html += '<ul>';
            processStartActivities.slice(0, 3).forEach(item => {
                html += `<li>${item.activity} (${item.count})</li>`;
            });
            html += '</ul>';
            
            // Top end activities
            html += '<h4>Top Ending Activities:</h4>';
            html += '<ul>';
            processEndActivities.slice(0, 3).forEach(item => {
                html += `<li>${item.activity} (${item.count})</li>`;
            });
            html += '</ul>';
            
            // Note: Process variants are not available from API data
            
            metricsContent.innerHTML = html;
        }
    }

    /**
     * Get incoming connections for an activity
     * @param {string} activity - Activity ID
     * @returns {Array} - Array of incoming connections
     */
    getIncomingConnections(activity) {
        return this.processData.edges.filter(edge => edge.target === activity);
    }

    /**
     * Get outgoing connections for an activity
     * @param {string} activity - Activity ID
     * @returns {Array} - Array of outgoing connections
     */
    getOutgoingConnections(activity) {
        return this.processData.edges.filter(edge => edge.source === activity);
    }

    /**
     * Resize graph when container size changes
     */
    resize() {
        if (this.isRendererReady()) {
            try {
                this.cy.resize();
                this.cy.fit();
            } catch (error) {
                console.warn('Resize failed:', error);
            }
        } else {
            console.warn('Cannot resize: Cytoscape renderer not ready');
        }
    }
    
    /**
     * Make ID safe for Graphviz by replacing problematic characters
     * @param {string} id - Original ID
     * @returns {string} - Safe ID for Graphviz
     */
    safeGraphvizId(id) {
        // Escape special characters and spaces
        return `"${id.replace(/"/g, '\\"')}"`;
    }
}

// Export class for use in other modules
window.GraphVisualizer = GraphVisualizer;